'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/forms/button';
import { Container } from '@/components/utils/Container';
import { useRouter } from 'next/navigation';
import { useSafeSearchParams } from '@/hooks/useSafeSearchParams';
import { JobsSearchExperience } from '@/components/features/jobs/JobsSearchExperience';
import { JobsLookupSearchPanel } from '@/components/features/jobs/JobsLookupSearchPanel';
import { isSearchConfirmShortcut } from '@/lib/keyboard-shortcuts';
import { PublicPageHero } from '@/components/features/public';

const COMMON_JOB_TITLES = [
  'Warehouse Worker',
  'Cashier',
  'Retail Sales Associate',
  'Delivery Driver',
  'Customer Service Representative',
  'Administrative Assistant',
  'Restaurant Server',
  'Cook',
  'Construction Worker',
  'Electrician',
  'Plumber',
  'Mechanic',
  'Truck Driver',
  'Machine Operator',
  'Production Worker',
  'Sales Associate',
  'Retail Manager',
  'Bookkeeper',
  'Medical Assistant',
  'Nursing Assistant',
  'Home Health Aide',
  'Hotel Front Desk Agent',
  'Bartender',
  'Package Handler',
  'Lab Technician',
  'Dental Assistant',
  'Fitness Trainer',
  'Flight Attendant',
  'Real Estate Agent',
  'Legal Assistant',
];

const POPULAR_US_CITIES = [
  'New York, NY',
  'Los Angeles, CA',
  'Chicago, IL',
  'Houston, TX',
  'Phoenix, AZ',
  'Philadelphia, PA',
  'San Diego, CA',
  'Dallas, TX',
  'San Jose, CA',
  'Austin, TX',
  'Jacksonville, FL',
  'Seattle, WA',
  'Denver, CO',
  'Washington, DC',
  'Boston, MA',
  'Nashville, TN',
  'Portland, OR',
  'Las Vegas, NV',
  'Atlanta, GA',
  'Miami, FL',
  'Minneapolis, MN',
  'New Orleans, LA',
  'Cleveland, OH',
  'Tampa, FL',
  'Honolulu, HI',
  'Kansas City, MO',
  'Orlando, FL',
  'Raleigh, NC',
  'Madison, WI',
  'Boise, ID',
];

const getRandomItems = <T,>(array: T[], count: number): T[] => {
  if (array.length <= count) {
    return [...array];
  }
  const copy = [...array];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, count);
};

const buildJobsUrl = (params: {
  title?: string;
  location?: string;
  titles?: string[];
  companies?: string[];
  page?: number;
}) => {
  const urlParams = new URLSearchParams();
  if (params.title?.trim()) urlParams.set('title', params.title.trim());
  if (params.location?.trim()) urlParams.set('location', params.location.trim());
  if (params.titles?.length) urlParams.set('titles', params.titles.join(','));
  if (params.companies?.length) urlParams.set('companies', params.companies.join(','));
  if (params.page) urlParams.set('page', Math.max(1, Math.floor(params.page)).toString());
  const queryString = urlParams.toString();
  return queryString ? `/jobs?${queryString}` : '/jobs';
};

export default function JobsPageClient() {
  const router = useRouter();
  const urlSearchParams = useSafeSearchParams();

  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [searchTitles, setSearchTitles] = useState<string[]>([]);
  const [titleInput, setTitleInput] = useState('');
  const [searchCompanies, setSearchCompanies] = useState<string[]>([]);
  const [companyInput, setCompanyInput] = useState('');
  const [userLocation, setUserLocation] = useState<string | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);
  const [suggestedKeywords] = useState(() => getRandomItems(COMMON_JOB_TITLES, 15));
  const [suggestedCities] = useState(() => getRandomItems(POPULAR_US_CITIES, 15));

  const urlParams = useMemo(() => {
    if (!urlSearchParams) {
      return { title: '', description: '', location: '', titles: [] as string[], companies: [] as string[] };
    }
    return {
      title: (urlSearchParams.get('title') || urlSearchParams.get('keyword') || '').trim(),
      location: (urlSearchParams.get('location') || '').trim(),
      titles: (urlSearchParams.get('titles') || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
      companies: (urlSearchParams.get('companies') || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
    };
  }, [urlSearchParams]);

  const hasSearchStarted = Boolean(
    urlParams.title ||
    urlParams.location ||
    urlParams.titles.length ||
    urlParams.companies.length
  );

  useEffect(() => {
    if (hasSearchStarted) return;
    setSearchKeyword(urlParams.title);
    setSearchLocation(urlParams.location);
    setSearchTitles(urlParams.titles);
    setSearchCompanies(urlParams.companies);
  }, [hasSearchStarted, urlParams.companies, urlParams.location, urlParams.title, urlParams.titles]);

  useEffect(() => {
    if (hasSearchStarted) {
      setIsLoadingLocation(false);
      return;
    }

    let isCancelled = false;
    const loadUserLocation = async () => {
      setIsLoadingLocation(true);
      try {
        const response = await fetch('/api/geo', { cache: 'no-store' });
        if (!response.ok) return;

        const geo = await response.json();
        const city = geo?.city;
        const state = geo?.region;
        const location = geo?.postal || (city && state ? `${city}, ${state}` : city || null);
        if (!isCancelled && location) {
          setUserLocation(location);
        }
      } catch (error) {
        console.error('Failed to fetch user location:', error);
      } finally {
        if (!isCancelled) {
          setIsLoadingLocation(false);
        }
      }
    };

    void loadUserLocation();

    return () => {
      isCancelled = true;
    };
  }, [hasSearchStarted]);

  const startSearch = useCallback((params: {
    title?: string;
    location?: string;
    titles?: string[];
    companies?: string[];
    page?: number;
  }) => {
    router.replace(buildJobsUrl(params), { scroll: false });
    requestAnimationFrame(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant',
      });
    });
  }, [router]);

  const addTitle = useCallback(() => {
    const value = titleInput.trim();
    if (
      value &&
      searchTitles.length < 5 &&
      !searchTitles.some((candidate) => candidate.toLowerCase() === value.toLowerCase())
    ) {
      setSearchTitles([...searchTitles, value]);
      setTitleInput('');
    }
  }, [searchTitles, titleInput]);

  const getSearchTitlesWithPendingInput = useCallback(() => {
    const value = titleInput.trim();
    if (!value) {
      return searchTitles;
    }
    if (searchTitles.some((candidate) => candidate.toLowerCase() === value.toLowerCase())) {
      return searchTitles;
    }
    if (searchTitles.length >= 5) {
      return searchTitles;
    }
    return [...searchTitles, value];
  }, [searchTitles, titleInput]);

  const addCompany = useCallback(() => {
    const value = companyInput.trim();
    if (
      value &&
      searchCompanies.length < 5 &&
      !searchCompanies.some((candidate) => candidate.toLowerCase() === value.toLowerCase())
    ) {
      setSearchCompanies([...searchCompanies, value]);
      setCompanyInput('');
    }
  }, [companyInput, searchCompanies]);

  const getSearchCompaniesWithPendingInput = useCallback(() => {
    const value = companyInput.trim();
    if (!value) {
      return searchCompanies;
    }
    if (searchCompanies.some((candidate) => candidate.toLowerCase() === value.toLowerCase())) {
      return searchCompanies;
    }
    if (searchCompanies.length >= 5) {
      return searchCompanies;
    }
    return [...searchCompanies, value];
  }, [companyInput, searchCompanies]);

  const handleLookupSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const titlesToSearch = getSearchTitlesWithPendingInput();
    const companiesToSearch = getSearchCompaniesWithPendingInput();
    setSearchTitles(titlesToSearch);
    setSearchCompanies(companiesToSearch);
    if (titleInput.trim()) {
      setTitleInput('');
    }
    if (companyInput.trim()) {
      setCompanyInput('');
    }
    startSearch({
      title: searchKeyword,
      location: searchLocation,
      titles: titlesToSearch,
      companies: companiesToSearch,
      page: 1,
    });
  }, [
    companyInput,
    getSearchCompaniesWithPendingInput,
    getSearchTitlesWithPendingInput,
    searchKeyword,
    searchLocation,
    startSearch,
    titleInput,
  ]);

  if (hasSearchStarted) {
    return (
      <div className="pt-20">
        <JobsSearchExperience
          basePath="/jobs"
          recommendedPath="/jobs"
          showTopTabs={false}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-28">
      <PublicPageHero
        eyebrow="Jobs"
        headline="Job board with a"
        accent="direct line"
        description="Browse millions of listings. Apply through the company link if you want, or skip the black hole and request direct contact information."
      />

      <Container className="pt-8 pb-12 max-w-5xl">
        <JobsLookupSearchPanel
          className="mt-4 mb-4 p-4 border border-border bg-card"
          searchKeyword={searchKeyword}
          onSearchKeywordChange={setSearchKeyword}
          searchLocation={searchLocation}
          onSearchLocationChange={setSearchLocation}
          searchTitles={searchTitles}
          titleInput={titleInput}
          onTitleInputChange={setTitleInput}
          onTitleInputKeyDown={(event) => {
            if (isSearchConfirmShortcut(event)) {
              return;
            }
            if (event.key === 'Enter') {
              event.preventDefault();
              addTitle();
            }
          }}
          onAddTitle={addTitle}
          onRemoveTitle={(title) => {
            setSearchTitles(searchTitles.filter((candidate) => candidate !== title));
          }}
          searchCompanies={searchCompanies}
          companyInput={companyInput}
          onCompanyInputChange={setCompanyInput}
          onCompanyInputKeyDown={(event) => {
            if (isSearchConfirmShortcut(event)) {
              return;
            }
            if (event.key === 'Enter') {
              event.preventDefault();
              addCompany();
            }
          }}
          onAddCompany={addCompany}
          onRemoveCompany={(company) => {
            setSearchCompanies(searchCompanies.filter((candidate) => candidate !== company));
          }}
          onSearchSubmit={handleLookupSubmit}
          onApplySearchHistory={(params) => {
            const titles = params.titles || [];
            const companies = params.companies || [];

            setSearchKeyword(params.title || '');
            setSearchLocation(params.location || '');
            setSearchTitles(titles);
            setTitleInput('');
            setSearchCompanies(companies);
            setCompanyInput('');

            startSearch({
              title: params.title,
              location: params.location,
              titles,
              companies,
              page: 1,
            });
          }}
          onClear={() => {
            setSearchKeyword('');
            setSearchLocation('');
            setSearchTitles([]);
            setTitleInput('');
            setSearchCompanies([]);
            setCompanyInput('');
            router.replace('/jobs', { scroll: true });
          }}
          isSearching={false}
        />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-4"
        >
          <div className="bg-card border border-border p-5 sm:p-6">
            <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-[0.18em] mb-4 text-center">
              Popular job titles
            </p>

            <div className="flex flex-wrap gap-2 justify-center">
              {suggestedKeywords.map((keyword, index) => (
                <motion.div
                  key={keyword}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.03 }}
                >
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSearchKeyword(keyword);
                      startSearch({
                        title: keyword,
                        location: searchLocation,
                        titles: searchTitles,
                        companies: searchCompanies,
                        page: 1,
                      });
                    }}
                  >
                    {keyword}
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="bg-card border border-border p-5 sm:p-6">
            <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-[0.18em] mb-4 text-center">
              Popular locations
            </p>

            <div className="flex flex-wrap gap-2 justify-center">
              {isLoadingLocation ? (
                <div className="bg-card border border-border px-3 py-1.5 text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <div className="w-3.5 h-3.5 border border-primary border-t-transparent animate-spin" />
                    <span className="text-muted-foreground">Finding your location...</span>
                  </div>
                </div>
              ) : userLocation ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => {
                      setSearchLocation(userLocation);
                      startSearch({
                        title: searchKeyword,
                        location: userLocation,
                        titles: searchTitles,
                        companies: searchCompanies,
                        page: 1,
                      });
                    }}
                  >
                    📍 {userLocation} · your location
                  </Button>
                </motion.div>
              ) : null}

              {suggestedCities.map((city, index) => (
                <motion.div
                  key={city}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: (userLocation ? 0.05 : 0) + index * 0.03 }}
                >
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSearchLocation(city);
                      startSearch({
                        title: searchKeyword,
                        location: city,
                        titles: searchTitles,
                        companies: searchCompanies,
                        page: 1,
                      });
                    }}
                  >
                    {city}
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </Container>
    </div>
  );
}
